import { useRef, useState } from "react";
import { Mic, Square, Sparkles, Loader2, Upload, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import { useRecorder } from "./useRecorder";

const PROMPTS = [
  ["Name & contact", "My name is ___, phone ___, email ___, I live in ___."],
  ["Certifications", "Certifications: EMT (date), CPR/BLS, Firefighter I, CPAT, driver license..."],
  ["Work history", "I worked at ___ as a ___ from ___ to ___. I was responsible for..."],
  ["Education", "Education: high school / college, degree or program, year..."],
  ["Skills", "Skills: Spanish, mechanical, customer service, teamwork..."],
  ["Volunteer", "Volunteer: ___ with ___ since ___. I helped with..."],
];

export function IntakePanel({ hasContent, onOrganized }) {
  const [text, setText] = useState("");
  const [replace, setReplace] = useState(false);
  const [working, setWorking] = useState(false);
  const [notes, setNotes] = useState([]);
  const fileRef = useRef(null);
  const rec = useRecorder((t) => setText((v) => (v ? v.trimEnd() + " " : "") + t));

  const organize = async () => {
    if (text.trim().length < 15) { toast.info("Add a few sentences first — who you are, what you've done."); return; }
    setWorking(true);
    try {
      const { data } = await api.post("/resume/organize", { text, replace });
      onOrganized(data);
      setNotes(data.notes || []);
      setText("");
      setReplace(false);
      toast.success("Resume organized — check the preview and tweak anything in Edit.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setWorking(false); }
  };

  const importFile = async (file) => {
    if (!file) return;
    setWorking(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/resume/import", form, { headers: { "Content-Type": "multipart/form-data" } });
      onOrganized(data);
      setNotes(data.notes || []);
      toast.success("Imported your resume. Edit anything, then pick a design.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setWorking(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="space-y-5" data-testid="resume-intake">
      <div>
        <h2 className="font-heading text-2xl text-slate-900">Just tell us about yourself.</h2>
        <p className="text-sm text-slate-500 mt-1">Type or talk like you would to a friend — jobs and dates, certifications, school, skills, volunteer work. We'll turn it into a clean, professional resume. You can add more anytime.</p>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="resume-prompt-chips">
        {PROMPTS.map(([label, hint]) => (
          <button key={label} type="button" onClick={() => setText((v) => (v ? v.trimEnd() + "\n" : "") + hint)} className="text-xs font-semibold px-3 py-1.5 rounded-full border border-slate-200 bg-white text-slate-600 hover:border-primary hover:text-primary transition-colors" data-testid={`resume-chip-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>+ {label}</button>
        ))}
      </div>
      <div className="relative">
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={9} placeholder={"Example: My name is Jake Morales, 559-555-0142, jake@email.com, Fresno CA. I got my EMT in March 2024 and passed the NREMT. I've worked for American Ambulance since April 2024 running 911 calls. Before that I was a lifeguard for the city 2021–2023..."} className="w-full rounded-xl border border-slate-200 bg-white p-4 pr-16 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary/30 resize-y" data-testid="resume-intake-text" />
        <button type="button" onClick={rec.toggle} disabled={rec.busy} title={rec.recording ? "Stop recording" : "Speak instead"} className={`absolute right-3 top-3 w-11 h-11 rounded-full flex items-center justify-center text-white shadow-md transition-colors ${rec.recording ? "bg-red-600 animate-pulse" : "bg-slate-900 hover:bg-primary"}`} data-testid="resume-mic-button">
          {rec.busy ? <Loader2 className="w-5 h-5 animate-spin" /> : rec.recording ? <Square className="w-4 h-4" /> : <Mic className="w-5 h-5" />}
        </button>
        {(rec.recording || rec.busy) && <p className="absolute right-16 top-5 text-xs font-semibold text-slate-500" data-testid="resume-mic-status">{rec.busy ? "Transcribing…" : "Listening — tap to stop"}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={organize} disabled={working} className="bg-primary hover:bg-primary/90 h-11 px-6" data-testid="resume-organize-button">
          {working ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}{hasContent ? "Add to my resume" : "Build my resume"}
        </Button>
        {hasContent && (
          <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer" data-testid="resume-replace-toggle">
            <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} className="accent-red-600" /> Start fresh (replace what I have)
          </label>
        )}
        <span className="text-xs text-slate-400 ml-auto">or</span>
        <input ref={fileRef} type="file" accept=".pdf,.docx,.txt" className="hidden" onChange={(e) => importFile(e.target.files?.[0])} data-testid="resume-import-input" />
        <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={working} className="h-11" data-testid="resume-import-button"><Upload className="w-4 h-4 mr-2" /> Upload an existing resume</Button>
      </div>
      {notes.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="resume-notes">
          <p className="text-[11px] font-bold uppercase tracking-wider text-amber-700 flex items-center gap-1.5 mb-2"><Lightbulb className="w-3.5 h-3.5" /> To make it stronger</p>
          <ul className="space-y-1.5 text-sm text-slate-700 list-disc pl-5">{notes.map((n, i) => <li key={i}>{n}</li>)}</ul>
        </div>
      )}
    </div>
  );
}
